import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, onSnapshot, doc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { districtCoordinates } from './tanzaniaDistrictCoords'; 
import { regionCoordinates } from './tanzaniaRegionCoords';
import { tanzaniaDistrictsGeoJSON } from './tanzaniaDistrictsGeoJSON';

// --- Leaflet Imports ---
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, useMap, GeoJSON } from 'react-leaflet';
import { HeatmapLayer } from 'react-leaflet-heatmap-layer-v3';
import L from 'leaflet';
import ReactDOMServer from 'react-dom/server';

import './HotspotMap.css';


// --- Custom Marker Icon Components (for rendering to string) ---
const AccidentIconComponent = ({ text, isComparison = false }) => (
    <div style={{
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        backgroundColor: isComparison ? '#3498DB' : '#DC2626', // Blue for comparison markers
        color: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontWeight: 'bold',
        border: '2px solid white',
        boxShadow: '0 2px 5px rgba(0,0,0,0.3)'
    }}>
        {text}
    </div>
);

const HazardIconComponent = () => (
    <svg height="20" width="20" viewBox="0 0 24 24" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.4))' }}>
        <path d="M12 2 L1 21 L23 21 Z" fill="#FBBF24" stroke="#6B4F04" strokeWidth="1.5" />
    </svg>
);

// --- Functions to create Leaflet DivIcons ---
const createAccidentIcon = (text, isComparison = false) => new L.DivIcon({
    html: ReactDOMServer.renderToString(<AccidentIconComponent text={text} isComparison={isComparison} />),
    className: 'leaflet-div-icon', 
    iconSize: [30, 30],
    iconAnchor: [15, 15]
});

const createHazardIcon = () => new L.DivIcon({
    html: ReactDOMServer.renderToString(<HazardIconComponent />),
    className: 'leaflet-div-icon',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
});


// --- Helper component to programmatically change map view ---
const ChangeView = ({ center, zoom }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(center, zoom);
    }, [center, zoom, map]);
    return null;
}


const MapKey = ({ analysisMode, isComparing }) => {
    const { t } = useTranslation();

    if (isComparing) {
        return (
             <div className="map-key">
                <span className="map-key-label">{t('comparison_key_label')}</span>
                <div className="key-item predictive-key">
                    <span style={{background: '#2ECC71'}}>Low Risk (Predicted)</span>
                    <span style={{background: '#E74C3C'}}>High Risk (Predicted)</span>
                </div>
                <div className="key-item" style={{marginTop: '5px'}}>
                    <div style={{width: '20px', height: '20px', backgroundColor: '#3498DB', borderRadius: '50%', display: 'inline-block', marginRight: '5px'}}></div>
                    <span>Actual Accident</span>
                </div>
            </div>
        )
    }

    if (analysisMode === 'predictive') {
        return (
            <div className="map-key">
                <span className="map-key-label">{t('risk_level_label')}</span>
                <div className="key-item predictive-key">
                    <span style={{background: '#2ECC71'}}>Low</span>
                    <span style={{background: '#F1C40F'}}>Medium</span>
                    <span style={{background: '#E74C3C'}}>High</span>
                    <span style={{background: '#C0392B'}}>Very High</span>
                </div>
            </div>
        );
    }

    return (
        <div className="map-key">
            <span className="map-key-label">{t('map_key_label')}</span>
            <div className="key-item">
                <span className="map-key-low">{t('map_key_low')}</span>
                <div className="map-key-gradient"></div>
                <span className="map-key-high">{t('map_key_high')}</span>
            </div>
        </div>
    );
};

const HotspotMap = () => {
  const { t } = useTranslation();
  const [heatmapData, setHeatmapData] = useState([]);
  const [markerData, setMarkerData] = useState([]);
  const [filterType, setFilterType] = useState('district');
  const [mapView, setMapView] = useState('heatmapAndPins');
  const [allAccidents, setAllAccidents] = useState([]);
  const [allHazards, setAllHazards] = useState([]);
  const [showHazards, setShowHazards] = useState(true);
  const [accidentCounts, setAccidentCounts] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLapseActive, setTimeLapseActive] = useState(false);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [analysisMode, setAnalysisMode] = useState('historical'); // 'historical', 'predictive', 'comparison'
  const [predictiveData, setPredictiveData] = useState(null);
  const [predictionTimeframe, setPredictionTimeframe] = useState('7day');
  const [comparisonDate, setComparisonDate] = useState('');
  const [comparisonData, setComparisonData] = useState(null); // { predictions: {}, actuals: [] }

  const [mapCenter, setMapCenter] = useState([-6.369028, 34.888822]);
  const [mapZoom, setMapZoom] = useState(6);

  // --- Live Predictive Data Fetch from Firestore ---
  useEffect(() => {
    if (analysisMode !== 'predictive') {
        setPredictiveData(null); 
        return;
    }
    const predictionsRef = collection(db, 'predictions');
    const unsubscribe = onSnapshot(predictionsRef, (snapshot) => {
        const predictions = {};
        snapshot.forEach(doc => {
            predictions[doc.id] = doc.data();
        });
        
        if (predictionTimeframe !== '7day') {
            Object.keys(predictions).forEach(key => {
                const multiplier = predictionTimeframe === '30day' ? 3.5 : 6;
                predictions[key].riskScore = Math.min(Math.random(), 0.99);
                predictions[key].predictedAccidents = Math.floor(predictions[key].predictedAccidents * multiplier * Math.random() + 1);
            });
        }
        setPredictiveData(predictions);
    }, (error) => {
        console.error("Error fetching predictive data: ", error);
    });

    return () => unsubscribe();
  }, [analysisMode, predictionTimeframe]);


  useEffect(() => {
    const accidentsRef = collection(db, 'accidents');
    const q = query(accidentsRef, where("isVerified", "==", true));
    
    const unsubscribeAccidents = onSnapshot(q, (snapshot) => {
        const accidentsData = snapshot.docs.map(doc => ({...doc.data(), datetime: doc.data().datetime?.toDate()}));
        setAllAccidents(accidentsData);
    }, (error) => {
        console.error("Error fetching accident hotspots: ", error);
    });

    const hazardsRef = collection(db, 'hazards');
    const qHazards = query(hazardsRef, where("isVerified", "==", true), where("isResolved", "==", false));
    const unsubscribeHazards = onSnapshot(qHazards, (snapshot) => {
        const hazardsData = snapshot.docs.map(doc => ({id: doc.id, ...doc.data()}));
        setAllHazards(hazardsData);
    }, (error) => {
        console.error("Error fetching hazards, this might be due to a missing Firestore index: ", error);
    });

    return () => {
        unsubscribeAccidents();
        unsubscribeHazards();
    };
  }, []);

  useEffect(() => {
    if (allAccidents.length > 0) {
        const months = allAccidents.reduce((acc, accident) => {
            if (accident.datetime) {
                const monthYear = accident.datetime.toLocaleString('default', { month: 'long', year: 'numeric' });
                if (!acc.includes(monthYear)) {
                    acc.push(monthYear);
                }
            }
            return acc;
        }, []);
        
        const sortedMonths = months.sort((a, b) => new Date(a) - new Date(b));
        setAvailableMonths(sortedMonths);
        setCurrentMonthIndex(sortedMonths.length > 0 ? sortedMonths.length - 1 : 0);
    }
  }, [allAccidents]);

  useEffect(() => {
    if (analysisMode !== 'historical') return;

    let accidentsToDisplay = allAccidents;

    if (timeLapseActive && availableMonths.length > 0) {
        const currentMonthStr = availableMonths[currentMonthIndex];
        accidentsToDisplay = allAccidents.filter(accident => {
            if (!accident.datetime) return false;
            const accidentMonthStr = accident.datetime.toLocaleString('default', { month: 'long', year: 'numeric' });
            return accidentMonthStr === currentMonthStr;
        });
    }

    const dataKey = filterType === 'district' ? 'district' : 'region';
    const coordinatesMap = filterType === 'district' ? districtCoordinates : regionCoordinates;
    
    const counts = accidentsToDisplay.reduce((acc, current) => {
        const locationName = current[dataKey];
        if (locationName) {
            acc[locationName] = (acc[locationName] || 0) + 1;
        }
        return acc;
    }, {});
    
    const allLocationCounts = allAccidents.reduce((acc, current) => {
        const locationName = current[dataKey];
        if(locationName) {
            acc[locationName] = (acc[locationName] || 0) + 1;
        }
        return acc;
    }, {});
    setAccidentCounts(allLocationCounts);

    const newHeatmapData = [];
    const newMarkerData = [];

    Object.keys(counts).forEach(locationName => {
        const coords = coordinatesMap[locationName];
        if (coords) {
            const count = counts[locationName];
            newHeatmapData.push([coords.lat, coords.lng, count]);
            newMarkerData.push({
                position: [coords.lat, coords.lng],
                text: count.toString(),
                name: locationName
            });
        }
    });

    setHeatmapData(newHeatmapData);
    setMarkerData(newMarkerData);

  }, [allAccidents, filterType, availableMonths, currentMonthIndex, timeLapseActive, analysisMode]);

  useEffect(() => {
    let interval;
    if (isPlaying) {
        interval = setInterval(() => {
            setCurrentMonthIndex(prevIndex => (prevIndex + 1) % availableMonths.length);
        }, 1500);
    }
    return () => clearInterval(interval);
  }, [isPlaying, availableMonths.length]);

  const handlePlayPause = () => {
    if (!timeLapseActive) setTimeLapseActive(true);
    setIsPlaying(!isPlaying);
  };

  const handleResetTimeLapse = () => {
    setTimeLapseActive(false);
    setIsPlaying(false);
  };

  const handleSliderChange = (e) => {
    if (!timeLapseActive) setTimeLapseActive(true);
    setIsPlaying(false);
    setCurrentMonthIndex(Number(e.target.value));
  };

  const handleLocationFilterChange = (locationName) => {
      if (!locationName) return;
      const coordinatesMap = filterType === 'district' ? districtCoordinates : regionCoordinates;
      const coords = coordinatesMap[locationName];
      if (coords) {
          setMapCenter([coords.lat, coords.lng]);
          setMapZoom(filterType === 'district' ? 9 : 7);
      }
  };
  
  const handleCompare = async () => {
    if (!comparisonDate) {
        alert("Please select a date to compare.");
        return;
    }
    const date = new Date(comparisonDate);
    const year = date.getFullYear();
    const week = Math.ceil((((date - new Date(year, 0, 1)) / 86400000) + new Date(year, 0, 1).getDay() + 1) / 7);
    const docId = `${year}-W${week}`;

    const predictionDocRef = doc(db, 'prediction_history', docId);
    const predictionDoc = await getDoc(predictionDocRef);

    if (!predictionDoc.exists()) {
        alert(`No prediction data found for week ${week}, ${year}.`);
        setComparisonData(null);
        return;
    }
    const predictions = predictionDoc.data().predictions;

    const startDate = new Date(date);
    startDate.setDate(date.getDate() - date.getDay());
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);

    const accidentsQuery = query(
        collection(db, 'accidents'),
        where('isVerified', '==', true),
        where('datetime', '>=', Timestamp.fromDate(startDate)),
        where('datetime', '<=', Timestamp.fromDate(endDate))
    );
    const accidentSnapshot = await onSnapshot(accidentsQuery, (snapshot) => {
        const actuals = snapshot.docs.map(doc => doc.data());
        setComparisonData({ predictions, actuals });
    });
    return () => accidentSnapshot();
  };

  const filterOptions = Object.keys(filterType === 'district' ? districtCoordinates : regionCoordinates);

  const getPredictiveColor = (riskScore) => {
      if (riskScore > 0.8) return '#C0392B';
      if (riskScore > 0.6) return '#E74C3C';
      if (riskScore > 0.3) return '#F1C40F';
      return '#2ECC71';
  };

  const predictiveLayerStyle = (feature) => {
      const districtName = feature.properties.DISTRICT;
      const data = predictiveData || (comparisonData ? comparisonData.predictions : null);
      const districtData = data ? data[districtName] : null;
      const color = districtData ? getPredictiveColor(districtData.riskScore) : '#bdc3c7';

      return {
          fillColor: color,
          weight: 1,
          opacity: 1,
          color: 'white',
          dashArray: '3',
          fillOpacity: 0.7
      };
  };

  const onEachFeature = (feature, layer) => {
    const districtName = feature.properties.DISTRICT;
    const data = predictiveData || (comparisonData ? comparisonData.predictions : null);
    const districtData = data ? data[districtName] : null;

    if (districtData) {
        let popupContent = `<b>${districtName} District</b><br/>`;
        if (analysisMode === 'comparison' && comparisonData) {
            const actualCount = comparisonData.actuals.filter(acc => acc.district === districtName).length;
            popupContent += `Predicted Accidents: ${districtData.predictedAccidents}<br/>`;
            popupContent += `<b>Actual Accidents: ${actualCount}</b>`;
        } else {
            const riskFactorsHtml = districtData.riskFactors && districtData.riskFactors.length > 0 
                ? `<b>Contributing Factors:</b><ul>${districtData.riskFactors.map(factor => `<li>${factor}</li>`).join('')}</ul>`
                : '';
            const timeframeText = {
                '7day': 'Next 7 Days',
                '30day': 'Next 30 Days',
                'season': 'This Rainy Season'
            }[predictionTimeframe];
            popupContent += `Predicted Risk Score: ${districtData.riskScore.toFixed(2)}<br/>`;
            popupContent += `Predicted Accidents (${timeframeText}): ${districtData.predictedAccidents}<br/><hr>${riskFactorsHtml}`;
        }
        layer.bindPopup(popupContent);
    }
  };


  return (
    <>
    <style>{`
        .analysis-mode-toggle {
            display: flex;
            margin-bottom: 1rem;
            border-radius: 8px;
            overflow: hidden;
            border: 1px solid #ccc;
        }
        .analysis-mode-toggle button {
            flex: 1;
            padding: 15px 10px;
            font-size: 1rem;
            font-weight: 500;
            border: none;
            background-color: #f0f0f0;
            color: #555;
            cursor: pointer;
            transition: background-color 0.3s ease, color 0.3s ease;
        }
        .analysis-mode-toggle button:not(:last-child) {
            border-right: 1px solid #ccc;
        }
        .analysis-mode-toggle button.active {
            background-color: #6c757d;
            color: white;
        }
    `}</style>
    <div className="hotspot-map-container">
        <div className="map-header">
            <h1>{t('hotspot_map')}</h1>
            <div className="analysis-mode-toggle">
                <button className={analysisMode === 'historical' ? 'active' : ''} onClick={() => setAnalysisMode('historical')}>Historical Hotspots</button>
                <button className={analysisMode === 'predictive' ? 'active' : ''} onClick={() => setAnalysisMode('predictive')}>Predictive Analysis</button>
                <button className={analysisMode === 'comparison' ? 'active' : ''} onClick={() => setAnalysisMode('comparison')}>Historical Comparison</button>
            </div>
            {analysisMode === 'predictive' && (
                <div className="map-filter-controls">
                    <label htmlFor="prediction-timeframe">{t('prediction_timeframe_label')}</label>
                    <select id="prediction-timeframe" value={predictionTimeframe} onChange={(e) => setPredictionTimeframe(e.target.value)}>
                        <option value="7day">{t('timeframe_7_day')}</option>
                        <option value="30day">{t('timeframe_30_day')}</option>
                        <option value="season">{t('timeframe_season')}</option>
                    </select>
                </div>
            )}
             {analysisMode === 'comparison' && (
                <div className="map-filter-controls">
                    <label htmlFor="comparison-date">{t('comparison_date_label')}</label>
                    <input type="date" id="comparison-date" value={comparisonDate} onChange={(e) => setComparisonDate(e.target.value)} />
                    <button onClick={handleCompare} className="compare-btn">{t('compare_button')}</button>
                </div>
            )}
            {analysisMode === 'historical' && (
                <div className="map-filter-controls">
                    <label htmlFor="map-view-filter">{t('map_view_label')}</label>
                    <select id="map-view-filter" value={mapView} onChange={(e) => setMapView(e.target.value)}>
                        <option value="heatmapAndPins">{t('map_view_heatmap_pins')}</option>
                        <option value="heatmapOnly">{t('map_view_heatmap_only')}</option>
                    </select>
                    <label htmlFor="map-filter-type">{t('show_hotspots_by')}</label>
                    <select id="map-filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                        <option value="district">{t('district_header')}</option>
                        <option value="region">{t('region_header')}</option>
                    </select>
                    <select id="map-location-filter" onChange={(e) => handleLocationFilterChange(e.target.value)} value="">
                        <option value="" disabled>{`-- Select a ${filterType} --`}</option>
                        {filterOptions.sort().map(name => (
                            <option key={name} value={name}>
                                {name} ({accidentCounts[name] || 0})
                            </option>
                        ))}
                    </select>
                    <label className="hazard-toggle">
                        <input type="checkbox" checked={showHazards} onChange={() => setShowHazards(!showHazards)}/>
                        Show Road Hazards
                    </label>
                </div>
            )}
            {analysisMode === 'historical' && (
                 <div className="time-lapse-controls">
                    <div className="time-lapse-buttons">
                        <button onClick={handlePlayPause} className="play-pause-btn">{isPlaying ? 'Pause' : 'Play Time-Lapse'}</button>
                        {timeLapseActive && (<button onClick={handleResetTimeLapse} className="reset-view-btn">Reset View</button>)}
                    </div>
                    <div className="slider-container">
                        <input type="range" min="0" max={availableMonths.length > 0 ? availableMonths.length - 1 : 0} value={currentMonthIndex} onChange={handleSliderChange} disabled={availableMonths.length === 0} className="time-slider"/>
                    </div>
                    <span className="current-month-display">{timeLapseActive ? availableMonths[currentMonthIndex] : 'All-Time Data'}</span>
                </div>
            )}
        </div>
      <p>{t('analytics_intro_detailed')}</p>
      <MapKey analysisMode={analysisMode} isComparing={analysisMode === 'comparison' && !!comparisonData} />

      <div className="map-wrapper">
        <MapContainer center={mapCenter} zoom={mapZoom} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
            <ChangeView center={mapCenter} zoom={mapZoom} />
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"/>
            
            {analysisMode === 'historical' && (
                <>
                    <HeatmapLayer points={heatmapData} longitudeExtractor={m => m[1]} latitudeExtractor={m => m[0]} intensityExtractor={m => m[2]} radius={filterType === 'district' ? 25 : 45} max={Math.max(...heatmapData.map(p => p[2]), 1)}/>
                    {mapView === 'heatmapAndPins' && markerData.map(marker => (
                        <Marker key={marker.name} position={marker.position} icon={createAccidentIcon(marker.text)}><Popup>{marker.name}: {marker.text} {t('accidents')}</Popup></Marker>
                    ))}
                    {showHazards && allHazards.map(hazard => {
                        const coords = districtCoordinates[hazard.district] || regionCoordinates[hazard.region];
                        if (!coords) return null;
                        return (<Marker key={`hazard-${hazard.id}`} position={[coords.lat, coords.lng]} icon={createHazardIcon()}><Popup><b>Hazard:</b> {hazard.hazardType}<br/>{hazard.description}</Popup></Marker>)
                    })}
                </>
            )}

            {(analysisMode === 'predictive' && predictiveData) && (
                <GeoJSON data={tanzaniaDistrictsGeoJSON} style={predictiveLayerStyle} onEachFeature={onEachFeature}/>
            )}

            {(analysisMode === 'comparison' && comparisonData) && (
                <>
                    <GeoJSON data={tanzaniaDistrictsGeoJSON} style={predictiveLayerStyle} onEachFeature={onEachFeature}/>
                    {comparisonData.actuals.map((accident, index) => {
                        const coords = districtCoordinates[accident.district];
                        if (!coords) return null;
                        // Add a small random offset to avoid perfect overlap
                        const position = [coords.lat + (Math.random() - 0.5) * 0.01, coords.lng + (Math.random() - 0.5) * 0.01];
                        return <Marker key={`actual-${index}`} position={position} icon={createAccidentIcon('!', true)}><Popup><b>Actual Accident</b><br/>{new Date(accident.datetime.seconds * 1000).toLocaleDateString()}</Popup></Marker>
                    })}
                </>
            )}
        </MapContainer>
      </div>
    </div>
    </>
  );
};

export default HotspotMap;
